/**
 * Pointer gestures for a timed {@link ActivityBlock}: drag-to-move (with a click-vs-drag threshold) and
 * drag-the-bottom-edge-to-resize.
 *
 * Both gestures mutate the block's DOM node directly (transform / height) for smooth 60fps feedback and
 * only commit to the board model on mouseup — move reports a whole-day shift + fractional hour shift,
 * resize reports the hour delta. A move under the 4px threshold is treated as a click and opens the part.
 */
import { HOUR_PX } from '../model';

interface UseBlockDragOptions {
  ref: React.RefObject<HTMLDivElement | null>;
  uid: string;
  /** Current rendered block height (px) — the resize preview grows/shrinks from here. */
  height: number;
  readOnly: boolean;
  onMove: (uid: string, dayShift: number, hourShift: number) => void;
  onResize: (uid: string, hourDelta: number) => void;
  onOpen: (uid: string) => void;
  onDragStateChange: (dragging: boolean) => void;
}

export function useBlockDrag({
  ref,
  uid,
  height,
  readOnly,
  onMove,
  onResize,
  onOpen,
  onDragStateChange,
}: UseBlockDragOptions) {
  const onBodyMouseDown = (event: React.MouseEvent) => {
    if (event.button !== 0 || readOnly) return;
    event.stopPropagation();
    const startX = event.clientX;
    const startY = event.clientY;
    let moved = false;
    let lastDx = 0;
    let lastDy = 0;
    const onMouseMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (!moved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
        moved = true;
        ref.current?.classList.add('tp-act--dragging');
        onDragStateChange(true);
      }
      if (moved) {
        lastDx = dx;
        lastDy = dy;
        if (ref.current) {
          ref.current.style.transform = `translate(${dx}px, ${dy}px)`;
          ref.current.style.zIndex = '50';
        }
      }
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onUp);
      if (ref.current) {
        ref.current.style.transform = '';
        ref.current.style.zIndex = '';
        ref.current.classList.remove('tp-act--dragging');
      }
      onDragStateChange(false);
      if (moved) {
        const colWidth = ref.current?.closest('.tp-col')?.clientWidth ?? 200;
        onMove(uid, Math.round(lastDx / colWidth), lastDy / HOUR_PX);
      } else {
        onOpen(uid);
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onUp);
  };

  const onResizeMouseDown = (event: React.MouseEvent) => {
    if (event.button !== 0 || readOnly) return;
    event.stopPropagation();
    const startY = event.clientY;
    let lastDelta = 0;
    const onMouseMove = (ev: MouseEvent) => {
      lastDelta = ev.clientY - startY;
      if (ref.current) ref.current.style.height = `${Math.max(20, height + lastDelta)}px`;
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onUp);
      if (ref.current) ref.current.style.height = '';
      onResize(uid, lastDelta / HOUR_PX);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onUp);
  };

  return { onBodyMouseDown, onResizeMouseDown };
}
